import { post, get } from "./http";
import { API_ENDPOINTS } from "@monorepo/config";

import type {
  IdentifyRequest,
  IdentifyResponse,
  VerifyEmailOtpRequest,
  VerifyEmailOtpResponse,
  ProfileRequest,
  ProfileResponse,
  VerifyPhoneOtpRequest,
  VerifyPhoneOtpResponse,
  SetPasswordRequest,
  SetPasswordResponse,
  TotpInitRequest,
  TotpInitResponse,
  TotpConfirmRequest,
  TotpConfirmResponse,
  WebAuthnRegisterInitRequest,
  WebAuthnRegisterInitResponse,
  WebAuthnRegisterCompleteRequest,
  WebAuthnRegisterCompleteResponse,
  SkipMfaResponse,
  BackupCodesGenerateRequest,
  BackupCodesGenerateResponse,
} from "../signup/types";

import type {
  SigninIdentifyRequest,
  SigninIdentifyResponse,
  SigninLoginRequest,
  SigninLoginResponse,
  SigninMfaMethod,
  SigninGetMfaMethodsResponse,
  SigninVerifyTotpRequest,
  SigninVerifyTotpResponse,
  SigninVerifyBackupCodeRequest,
  SigninVerifyBackupCodeResponse,
  SigninWebAuthnAssertionOptionsResponse,
  SigninVerifyWebAuthnAssertionResponse,
  SigninVerifyWebAuthnAssertionRequest,
  SigninForceChangePasswordResponse,
  SigninMfaSetupInitResponse,
  SigninMfaSetupConfirmResponse,
  SigninGenerateBackupCodesResponse,
  SigninConfirmBackupCodesResponse,
} from "../signin/types";

import type {
  PasswordResetRequestRequest,
  PasswordResetRequestResponse,
  PasswordResetValidateRequest,
  PasswordResetValidateResponse,
  PasswordResetConfirmRequest,
  PasswordResetConfirmResponse,
  LostAccessRequest,
  LostAccessResponse,
  RecoveryStatusResponse,
  RecoveryValidateRequest,
  RecoveryValidateResponse,
  RecoveryCompleteRequest,
  RecoveryCompleteResponse,
  RecoveryIdentifyRequest,
  RecoveryIdentifyResponse,
  GetRecoveryMethodsResponse,
  InitiateRecoveryRequest,
  InitiateRecoveryResponse,
  RecoveryVerifyIdentityRequest,
  RecoveryVerifyIdentityResponse,
  RecoveryVerifyIdentityOtpRequest,
  RecoveryVerifyIdentityOtpResponse,
  RecoveryVerifyAuthorityRequest,
  RecoveryVerifyAuthorityResponse,
  RecoveryResetPasswordRequest,
  RecoveryResetPasswordResponse,
} from "../recovery/types";

/**
 * Header key for flow-specific tokens (Signup/MFA State).
 * Used to avoid conflict with the main 'Authorization' header used for Frappe API access.
 */
const FLOW_TOKEN_HEADER = "X-Flow-Token";
const USER_INFO_HEADER = "X-Userinfo";

/**
 * Authentication API Client
 * Centralized typed API calls for Signup, Signin, and Recovery flows.
 */

// =============================================================================
// SIGNUP FLOW
// =============================================================================

/**
 * Step 1: Identify user (Signup)
 */
export async function identifyUser(
  data: IdentifyRequest
): Promise<IdentifyResponse> {
  return post<IdentifyResponse>(API_ENDPOINTS.SIGNUP_IDENTIFY, {
    email: data.email,
    captchaToken: data.captchaToken,
    plan: data.plan ?? null,
  });
}

/**
 * Step 2: Verify Email OTP
 */
export async function verifyEmailOtp(
  data: VerifyEmailOtpRequest,
  preUserToken?: string
): Promise<VerifyEmailOtpResponse> {
  const headers = preUserToken ? { [FLOW_TOKEN_HEADER]: preUserToken } : {};

  return post<VerifyEmailOtpResponse>(
    API_ENDPOINTS.SIGNUP_VERIFY_EMAIL_OTP,
    { otp: data.otp },
    { headers }
  );
}

/**
 * Ownership Verification: Validate OTP
 * Called when ownership_verification=true is returned from /initial/step.
 * The user already verified email before; this proves they still own it.
 */
export async function validateOwnershipOtp(
  otp: string,
  tempToken: string
): Promise<VerifyEmailOtpResponse> {
  return post<VerifyEmailOtpResponse>(
    API_ENDPOINTS.OWNERSHIP_OTP_VALIDATE,
    { otp },
    { headers: { [FLOW_TOKEN_HEADER]: tempToken } }
  );
}

export async function resendEmailOtp(email: string): Promise<IdentifyResponse> {
  return post<IdentifyResponse>(API_ENDPOINTS.SIGNUP_IDENTIFY, { email, plan: null });
}

/**
 * Step 3: Profile
 */
export async function submitProfile(
  data: ProfileRequest,
  preUserToken: string
): Promise<ProfileResponse> {
  return post<ProfileResponse>(
    API_ENDPOINTS.SIGNUP_PROFILE,
    {
      full_name: data.full_name,
      company_name: data.company_name,
      phone: data.phone,
    },
    {
      headers: { [FLOW_TOKEN_HEADER]: preUserToken },
    }
  );
}

/**
 * Step 4: Verify Phone OTP
 */
export async function verifyPhoneOtp(
  data: VerifyPhoneOtpRequest,
  preUserToken: string
): Promise<VerifyPhoneOtpResponse> {
  return post<VerifyPhoneOtpResponse>(
    API_ENDPOINTS.SIGNUP_VERIFY_PHONE_OTP,
    { otp: data.otp },
    {
      headers: { [FLOW_TOKEN_HEADER]: preUserToken },
    }
  );
}

export async function resendPhoneOtp(
  phone: string,
  preUserToken: string
): Promise<{ success: boolean; message: string }> {
  return post(
    API_ENDPOINTS.SIGNUP_RESEND_PHONE_OTP,
    { phone },
    {
      headers: { [FLOW_TOKEN_HEADER]: preUserToken },
    }
  );
}

/**
 * Step 5: Set Password
 */
export async function setPassword(
  data: SetPasswordRequest,
  preUserToken: string
): Promise<SetPasswordResponse> {
  return post<SetPasswordResponse>(
    API_ENDPOINTS.SIGNUP_SET_PASSWORD,
    { password: data.password },
    {
      headers: { [FLOW_TOKEN_HEADER]: preUserToken },
    }
  );
}

// =============================================================================
// SIGNUP MFA SETUP (Enrollment)
// =============================================================================

export async function initTotp(
  data: TotpInitRequest,
  preMfaToken: string
): Promise<TotpInitResponse> {
  return post<TotpInitResponse>(API_ENDPOINTS.TOTP_INIT, data, {
    headers: { [FLOW_TOKEN_HEADER]: preMfaToken },
  });
}

export async function confirmTotp(
  data: TotpConfirmRequest,
  preMfaToken: string
): Promise<TotpConfirmResponse> {
  return post<TotpConfirmResponse>(
    API_ENDPOINTS.TOTP_CONFIRM,
    { code: data.code },
    {
      headers: { [FLOW_TOKEN_HEADER]: preMfaToken },
    }
  );
}

export async function initWebAuthnRegistration(
  data: WebAuthnRegisterInitRequest,
  preMfaToken: string
): Promise<WebAuthnRegisterInitResponse> {
  return post<WebAuthnRegisterInitResponse>(
    API_ENDPOINTS.WEBAUTHN_REGISTER_INIT,
    data,
    {
      headers: { [FLOW_TOKEN_HEADER]: preMfaToken },
    }
  );
}

export async function completeWebAuthnRegistration(
  data: WebAuthnRegisterCompleteRequest,
  preMfaToken: string
): Promise<WebAuthnRegisterCompleteResponse> {
  return post<WebAuthnRegisterCompleteResponse>(
    API_ENDPOINTS.WEBAUTHN_REGISTER_COMPLETE,
    data,
    {
      headers: { [FLOW_TOKEN_HEADER]: preMfaToken },
    }
  );
}

export async function skipMfa(
  preMfaToken: string
): Promise<SkipMfaResponse> {
  return post<SkipMfaResponse>(
    API_ENDPOINTS.SIGNUP_MFA_SKIP,
    {},
    {
      headers: { [FLOW_TOKEN_HEADER]: preMfaToken },
    }
  );
}

export async function generateBackupCodes(
  data: BackupCodesGenerateRequest,
  preMfaToken: string
): Promise<BackupCodesGenerateResponse> {
  return post<BackupCodesGenerateResponse>(
    API_ENDPOINTS.BACKUP_CODES_GENERATE,
    data,
    {
      headers: { [FLOW_TOKEN_HEADER]: preMfaToken },
    }
  );
}

export async function confirmBackupCodes(
  preMfaToken: string
): Promise<{ success: boolean; message: string }> {
  return post(
    API_ENDPOINTS.BACKUP_CODES_CONFIRM,
    {}, // Empty body
    {
      headers: { [FLOW_TOKEN_HEADER]: preMfaToken },
    }
  );
}

// =============================================================================
// SIGNIN FLOW
// =============================================================================

/**
 * Signin Step 1: Identify
 */
export async function signinIdentify(
  data: SigninIdentifyRequest
): Promise<SigninIdentifyResponse> {
  return post<SigninIdentifyResponse>(API_ENDPOINTS.SIGNIN_IDENTIFY, {
    email: data.email,
    captchaToken: data.captchaToken,
  });
}

/**
 * Signin Step 2: Password
 */
export async function signinPassword(
  data: SigninLoginRequest
): Promise<SigninLoginResponse> {
  return post<SigninLoginResponse>(API_ENDPOINTS.SIGNIN_PASSWORD, {
    email: data.email,
    password: data.password,
    captchaToken: data.captchaToken,
    device_name: data.device_name,
  });
}

/**
 * Signin Step 3: MFA Method Selection
 */
export async function getMfaMethods(
  preMfaToken: string
): Promise<SigninMfaMethod[]> {
  return post<SigninGetMfaMethodsResponse>(
    API_ENDPOINTS.SIGNIN_MFA_METHODS,
    {},
    {
      headers: { [FLOW_TOKEN_HEADER]: preMfaToken },
    }
  ).then((res) => res.methods);
}

/**
 * Signin MFA: Verify TOTP
 */
export async function signinVerifyTotp(
  code: string,
  preMfaToken: string
): Promise<SigninVerifyTotpResponse> {
  const payload: SigninVerifyTotpRequest = {
    code,
    pre_mfa_token: preMfaToken,
  };

  return post<SigninVerifyTotpResponse>(
    API_ENDPOINTS.SIGNIN_VERIFY_TOTP,
    payload,
    {
      headers: { [FLOW_TOKEN_HEADER]: preMfaToken },
    }
  );
}

/**
 * Signin MFA: Verify Backup Code
 */
export async function signinVerifyBackupCode(
  code: string,
  preMfaToken: string
): Promise<SigninVerifyBackupCodeResponse> {
  const payload: SigninVerifyBackupCodeRequest = {
    code,
    pre_mfa_token: preMfaToken,
  };

  return post<SigninVerifyBackupCodeResponse>(
    API_ENDPOINTS.SIGNIN_VERIFY_BACKUP_CODE,
    payload,
    {
      headers: { [FLOW_TOKEN_HEADER]: preMfaToken },
    }
  );
}

/**
 * Signin MFA: WebAuthn Assertion Options
 */
export async function getWebAuthnAssertionOptions(
  preMfaToken: string
): Promise<SigninWebAuthnAssertionOptionsResponse> {
  return post<SigninWebAuthnAssertionOptionsResponse>(
    API_ENDPOINTS.SIGNIN_WEBAUTHN_ASSERTION,
    {},
    {
      headers: { [FLOW_TOKEN_HEADER]: preMfaToken },
    }
  );
}

/**
 * Signin MFA: WebAuthn Verify
 */
export async function verifyWebAuthnAssertion(
  credential: SigninVerifyWebAuthnAssertionRequest["credential"],
  preMfaToken: string
): Promise<SigninVerifyWebAuthnAssertionResponse> {
  const payload: SigninVerifyWebAuthnAssertionRequest = {
    pre_mfa_token: preMfaToken,
    credential,
  };

  return post<SigninVerifyWebAuthnAssertionResponse>(
    API_ENDPOINTS.SIGNIN_WEBAUTHN_VERIFY,
    payload,
    {
      headers: { [FLOW_TOKEN_HEADER]: preMfaToken },
    }
  );
}

// =============================================================================
// SIGNIN EMPLOYEE ONBOARDING (Force Change Password / MFA Setup)
// =============================================================================

export async function signinForceChangePassword(
  newPassword: string,
  preAuthToken: string
): Promise<SigninForceChangePasswordResponse> {
  return post<SigninForceChangePasswordResponse>(
    API_ENDPOINTS.SIGNIN_FORCE_CHANGE_PASSWORD,
    { new_password: newPassword },
    { headers: { [FLOW_TOKEN_HEADER]: preAuthToken } }
  );
}

export async function signinInitMfaSetup(
  preAuthToken: string
): Promise<SigninMfaSetupInitResponse> {
  return post<SigninMfaSetupInitResponse>(
    API_ENDPOINTS.SIGNIN_MFA_SETUP_INIT,
    {},
    { headers: { [FLOW_TOKEN_HEADER]: preAuthToken } }
  );
}

export async function signinCompleteMfaSetup(
  code: string,
  preAuthToken: string
): Promise<SigninMfaSetupConfirmResponse> {
  return post<SigninMfaSetupConfirmResponse>(
    API_ENDPOINTS.SIGNIN_MFA_SETUP_CONFIRM,
    { code },
    { headers: { [FLOW_TOKEN_HEADER]: preAuthToken } }
  );
}

export async function signinGenerateBackupCodes(
  preAuthToken: string
): Promise<SigninGenerateBackupCodesResponse> {
  return post<SigninGenerateBackupCodesResponse>(
    API_ENDPOINTS.SIGNIN_GENERATE_BACKUP_CODES,
    {},
    { headers: { [FLOW_TOKEN_HEADER]: preAuthToken } }
  );
}

export async function signinConfirmBackupCodes(
  preAuthToken: string
): Promise<SigninConfirmBackupCodesResponse> {
  return post<SigninConfirmBackupCodesResponse>(
    API_ENDPOINTS.SIGNIN_CONFIRM_BACKUP_CODES,
    {},
    { headers: { [FLOW_TOKEN_HEADER]: preAuthToken } }
  );
}

export async function signinSkipMfa(
  preAuthToken: string,
  email?: string
): Promise<SkipMfaResponse> {
  const headers: Record<string, string> = {
    [FLOW_TOKEN_HEADER]: preAuthToken,
  };

  if (email) {
    try {
      // Gateways often expect X-Userinfo to be a Base64-encoded JSON object
      headers[USER_INFO_HEADER] = btoa(JSON.stringify({ email }));
    } catch (e) {
      headers[USER_INFO_HEADER] = email;
    }
  }

  return post<SkipMfaResponse>(API_ENDPOINTS.SIGNIN_MFA_SKIP, {}, { headers });
}

/**
 * Get the final redirect URL after a successful sign-in.
 * Called as the last step in any auth flow to resolve where the user should land.
 */
export async function signinGetRedirectUrl(): Promise<{ status: string; redirect_to: string }> {
  return get<{ status: string; redirect_to: string }>(API_ENDPOINTS.SIGNIN_REDIRECT);
}

// =============================================================================
// ACCOUNT RECOVERY FLOW
// =============================================================================

export async function requestPasswordReset(
  data: PasswordResetRequestRequest
): Promise<PasswordResetRequestResponse> {
  return post<PasswordResetRequestResponse>(API_ENDPOINTS.RECOVERY_INIT, data);
}

export async function validatePasswordResetToken(
  data: PasswordResetValidateRequest
): Promise<PasswordResetValidateResponse> {
  return get<PasswordResetValidateResponse>(
    API_ENDPOINTS.RECOVERY_VERIFY_TOKEN,
    {
      headers: {
        [FLOW_TOKEN_HEADER]: data.token,
      },
    }
  );
}

export async function confirmPasswordReset(
  data: PasswordResetConfirmRequest
): Promise<PasswordResetConfirmResponse> {
  return post<PasswordResetConfirmResponse>(
    API_ENDPOINTS.RECOVERY_RESET_PASSWORD,
    data
  );
}

export async function createLostAccessRequest(
  data: LostAccessRequest
): Promise<LostAccessResponse> {
  return post<LostAccessResponse>(API_ENDPOINTS.RECOVERY_LOST_ACCESS, data);
}

export async function getRecoveryRequestStatus(
  requestId: string
): Promise<RecoveryStatusResponse> {
  return get<RecoveryStatusResponse>(
    API_ENDPOINTS.RECOVERY_REQUEST_STATUS, // URL should NOT have /${requestId}
    {
      params: {
        request_id: requestId, // Maps to ?request_id=...
      },
    }
  );
}

export async function validateRecoveryToken(
  data: RecoveryValidateRequest
): Promise<RecoveryValidateResponse> {
  return get<RecoveryValidateResponse>(API_ENDPOINTS.RECOVERY_VERIFY_TOKEN, {
    headers: {
      [FLOW_TOKEN_HEADER]: data.token,
    },
  });
}

export async function completeRecovery(
  data: RecoveryCompleteRequest
): Promise<RecoveryCompleteResponse> {
  const { token, ...payload } = data;
  return post<RecoveryCompleteResponse>(
    API_ENDPOINTS.RECOVERY_RESET_PASSWORD,
    payload,
    {
      headers: {
        [FLOW_TOKEN_HEADER || "X-Flow-Token"]: token,
      },
    }
  );
}

// =============================================================================
// ADVANCED ACCOUNT RECOVERY FLOW (6-Step)
// =============================================================================

interface FrappeResponse<T> {
  message: T;
}

const unwrap = <T>(res: any): T => {
  if (res && typeof res === "object" && "message" in res) {
    return res.message as T;
  }
  return res as T;
};

/** Step 1: Identify */
export async function recoveryIdentify(
  data: RecoveryIdentifyRequest
): Promise<RecoveryIdentifyResponse> {
  const res = await post<FrappeResponse<RecoveryIdentifyResponse>>(
    API_ENDPOINTS.RECOVERY_IDENTIFY,
    data
  );
  return unwrap<RecoveryIdentifyResponse>(res);
}

/** Step 2: Get Methods */
export async function recoveryGetMethods(
  preAuthToken: string
): Promise<GetRecoveryMethodsResponse> {
  const res = await post<FrappeResponse<GetRecoveryMethodsResponse>>(
    API_ENDPOINTS.RECOVERY_METHODS,
    {},
    { headers: { [FLOW_TOKEN_HEADER]: preAuthToken } }
  );
  return unwrap<GetRecoveryMethodsResponse>(res);
}

/** Step 3: Initiate (Select Method) */
export async function recoveryInitiate(
  data: InitiateRecoveryRequest,
  preAuthToken: string
): Promise<InitiateRecoveryResponse> {
  const res = await post<FrappeResponse<InitiateRecoveryResponse>>(
    API_ENDPOINTS.RECOVERY_MK_INITIATE,
    data,
    { headers: { [FLOW_TOKEN_HEADER]: preAuthToken } }
  );
  return unwrap<InitiateRecoveryResponse>(res);
}

/** Step 4: Verify Identity (Link) */
export async function recoveryVerifyIdentity(
  data: RecoveryVerifyIdentityRequest
): Promise<RecoveryVerifyIdentityResponse> {
  const res = await post<FrappeResponse<RecoveryVerifyIdentityResponse>>(
    API_ENDPOINTS.RECOVERY_VERIFY_IDENTITY,
    {},
    {
      headers: {
        [FLOW_TOKEN_HEADER]: data.reset_token,
      },
    }
  );
  const unwrapped = unwrap<RecoveryVerifyIdentityResponse>(res);
  if (unwrapped && (unwrapped as any).pre_auth_token && !unwrapped.session_token) {
    unwrapped.session_token = (unwrapped as any).pre_auth_token;
  }
  return unwrapped;
}

/** Step 4b: Verify Identity (OTP) */
export async function recoveryVerifyIdentityOtp(
  data: RecoveryVerifyIdentityOtpRequest,
  preAuthToken: string
): Promise<RecoveryVerifyIdentityOtpResponse> {
  const res = await post<FrappeResponse<RecoveryVerifyIdentityOtpResponse>>(
    API_ENDPOINTS.RECOVERY_VERIFY_IDENTITY,
    { otp: data.code },
    { headers: { [FLOW_TOKEN_HEADER]: preAuthToken } }
  );
  const unwrapped = unwrap<RecoveryVerifyIdentityOtpResponse>(res);
  if (unwrapped && (unwrapped as any).pre_auth_token && !unwrapped.session_token) {
    unwrapped.session_token = (unwrapped as any).pre_auth_token;
  }
  return unwrapped;
}

/** Step 5: Verify Authority (OTP) */
export async function recoveryVerifyAuthority(
  data: RecoveryVerifyAuthorityRequest,
  sessionToken: string
): Promise<RecoveryVerifyAuthorityResponse> {
  const res = await post<FrappeResponse<RecoveryVerifyAuthorityResponse>>(
    API_ENDPOINTS.RECOVERY_VERIFY_AUTHORITY,
    { otp: data.code },
    { headers: { [FLOW_TOKEN_HEADER]: sessionToken } }
  );
  const unwrapped = unwrap<RecoveryVerifyAuthorityResponse>(res);
  if (unwrapped && (unwrapped as any).pre_auth_token) {
    if (!unwrapped.verification_token) {
      unwrapped.verification_token = (unwrapped as any).pre_auth_token;
    }
    if (!unwrapped.reset_token) {
      unwrapped.reset_token = (unwrapped as any).pre_auth_token;
    }
  }
  return unwrapped;
}

/** Step 6: Reset Password */
export async function recoveryResetPassword(
  data: RecoveryResetPasswordRequest
): Promise<RecoveryResetPasswordResponse> {
  const res = await post<FrappeResponse<RecoveryResetPasswordResponse>>(
    API_ENDPOINTS.RECOVERY_RESET_PASSWORD,
    { new_password: data.new_password },
    { headers: { [FLOW_TOKEN_HEADER]: data.verification_token || data.reset_token } }
  );
  return unwrap<RecoveryResetPasswordResponse>(res);
}
